import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import MUIDataTable from "mui-datatables";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import ListItemText from "@material-ui/core/ListItemText";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import Alert from "@material-ui/lab/Alert";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useQuery } from "react-query";
import { fetchViajeProgramadoDetalle } from "../api/viajes";
import {
  fetchServiciosSolicitados,
  fetchViajesDeServicioSolicitado,
} from "../api/serviciosSolicitados";
import ViajeDetalleCard from "./ViajeDetalleCard";
import { downloadPageCsv } from "../utils/downloadCsv";
import {
  cerrarSwalTramitando,
  mostrarSwalTramitando,
} from "../utils/funcionesSwal";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function rowsPerPageOptionsFor(totalCount, perPage, allRows) {
  // The active page size represents "Todos" after that option is selected.
  // Before selection, use the current total as its value.
  const allRowsValue = allRows ? Math.max(perPage, 1) : Math.max(totalCount, 1);
  return [
    ...ROWS_PER_PAGE_OPTIONS.filter((option) => option !== allRowsValue),
    { label: "Todos", value: allRowsValue },
  ];
}

function PatientSummaryCell({ value, classes }) {
  const patients = Array.isArray(value) ? value.filter(Boolean) : [];
  if (!patients.length) return <Typography color="textSecondary">—</Typography>;

  return (
    <div className={classes.patientCell}>
      <Typography variant="body2" className={classes.patientCount}>
        {patients.length}
      </Typography>
      <Typography variant="body2" className={classes.patientNames}>
        {patients.join(" · ")}
      </Typography>
    </div>
  );
}

function PatientSearchField({ value, onChange, label, className }) {
  return (
    <TextField
      className={className}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      variant="outlined"
      size="small"
      label={label}
      placeholder="Nombre del paciente"
      inputProps={{ "aria-label": label }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );
}

function applyColumnVisibility(columns, hiddenColumns) {
  return columns.map((column) => ({
    ...column,
    options: {
      ...column.options,
      display:
        column.options?.display === "excluded"
          ? "excluded"
          : !hiddenColumns.includes(column.name),
    },
  }));
}

const useStyles = makeStyles((theme) => ({
  layout: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 64,
    right: 0,
    bottom: 0,
    left: 240,
    minWidth: 0,
    overflow: "hidden",
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down("sm")]: {
      left: 0,
    },
    [theme.breakpoints.down("xs")]: {
      top: 56,
      left: 0,
      padding: theme.spacing(2),
    },
  },
  title: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 700,
  },
  intro: {
    marginBottom: theme.spacing(2),
    maxWidth: 560,
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexShrink: 0,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
  },
  heading: {
    minWidth: 0,
  },
  tableControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    width: "100%",
    marginBottom: theme.spacing(1.5),
    flexShrink: 0,
  },
  tableActions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  tableToolbarSearch: {
    width: 280,
    minWidth: 180,
    flex: "0 1 280px",
    marginLeft: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
    },
    [theme.breakpoints.down("xs")]: {
      width: 180,
      minWidth: 0,
      flex: "0 1 180px",
      marginLeft: 0,
    },
  },
  serviceHeader: {
    flexShrink: 0,
    marginBottom: theme.spacing(2),
  },
  serviceHeading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      flexDirection: "column",
      gap: theme.spacing(0.5),
    },
  },
  tableTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  resultCount: {
    fontWeight: 700,
  },
  serviceCell: {
    backgroundColor: "rgba(63, 81, 181, 0.10)",
    color: theme.palette.primary.dark,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  mutuaCell: {
    backgroundColor: "rgba(0, 150, 136, 0.12)",
    color: theme.palette.secondary.dark,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  serviceTypeCell: {
    backgroundColor: "rgba(63, 81, 181, 0.06)",
    fontWeight: 600,
  },
  vehicleCell: {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
  },
  tripCountCell: {
    backgroundColor: "rgba(63, 81, 181, 0.05)",
    textAlign: "center",
    fontWeight: 700,
  },
  tripCode: {
    color: theme.palette.primary.dark,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  tripDate: {
    whiteSpace: "nowrap",
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      whiteSpace: "normal",
      overflowWrap: "anywhere",
    },
  },
  serviceId: {
    color: theme.palette.primary.dark,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  serviceType: {
    fontWeight: 600,
  },
  patientCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  patientCount: {
    flexShrink: 0,
    color: theme.palette.primary.dark,
    fontWeight: 700,
    minWidth: 18,
    textAlign: "center",
  },
  patientNames: {
    minWidth: 0,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  dateCell: {
    whiteSpace: "nowrap",
    fontWeight: 500,
  },
  cellId: {
    width: 105,
    minWidth: 105,
    whiteSpace: "nowrap",
  },
  cellType: {
    width: 132,
    minWidth: 132,
  },
  cellVehicle: {
    width: 125,
    minWidth: 125,
  },
  cellCount: {
    width: 84,
    minWidth: 84,
    textAlign: "center",
  },
  subtitle: {
    marginBottom: theme.spacing(2),
    overflowWrap: "anywhere",
  },
  table: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "100%",
    width: "100%",
    minWidth: 0,
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    "& tbody tr.MuiTableRow-root": {
      cursor: "pointer",
    },
    "& > .MuiPaper-root": {
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      flex: "0 1 auto",
      minHeight: 0,
      maxHeight: "100%",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[1],
      overflow: "hidden",
    },
    "& > .MuiPaper-root > :not([class*='responsiveBase'])": {
      flexShrink: 0,
    },
    // mui-datatables places pagination outside this scrollable body.
    "& > .MuiPaper-root > [class*='responsiveBase']": {
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "auto",
    },
    "& .MuiToolbar-root": {
      minHeight: 56,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    "& .MuiTableHead-root .MuiTableCell-root:not(:last-child), & .MuiTableBody-root .MuiTableCell-body:not(:last-child)": {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiTableHead-root .MuiTableCell-head": {
      backgroundColor: "#f4f6fa",
      color: theme.palette.text.secondary,
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.035em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    },
    "& .MuiTableBody-root .MuiTableCell-body": {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: "middle",
    },
    // MUIDataTable copies the cell class to its responsive label/value wrappers.
    // Keep the tint on the <td> itself so the whole column reads as one cell.
    "& .MuiTableBody-root .MuiTableCell-body > div": {
      backgroundColor: "transparent !important",
    },
    "& .MuiTablePagination-root": {
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[50],
      flexShrink: 0,
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: "rgba(63, 81, 181, 0.045)",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 120ms ease-in-out",
    },
    [theme.breakpoints.down("sm")]: {
      // Inline desktop column widths must not constrain stacked mobile cells.
      "& .MuiTableBody-root .MuiTableCell-body": {
        display: "grid",
        gridTemplateColumns: "96px minmax(0, 1fr)",
        alignItems: "start",
        gap: theme.spacing(1),
        width: "100% !important",
        minWidth: "0 !important",
        whiteSpace: "normal !important",
        textAlign: "left !important",
        borderRight: 0,
      },
      "& .MuiTableBody-root .MuiTableCell-body > div": {
        display: "block",
        width: "auto",
        minWidth: 0,
        fontSize: "0.875rem",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
      },
      "& .MuiTableBody-root .MuiTableCell-body > div:first-child": {
        color: theme.palette.text.secondary,
        fontWeight: 500,
      },
      "& .MuiTableFooter-root .MuiTableCell-footer": {
        padding: 0,
      },
      "& .MuiTablePagination-root": {
        width: "100%",
        minWidth: 0,
      },
      "& .MuiTablePagination-toolbar": {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        columnGap: theme.spacing(1),
        rowGap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1.5),
      },
      "& .MuiTablePagination-spacer": {
        display: "none",
      },
      "& .MuiTablePagination-selectRoot": {
        justifySelf: "end",
        margin: 0,
      },
      "& .MuiTablePagination-actions": {
        marginLeft: 0,
      },
    },
  },
  detailLayout: {
    overflowY: "auto",
  },
  backButton: {
    marginBottom: theme.spacing(2),
  },
  progress: {
    marginBottom: theme.spacing(1),
  },
}));

function tableOptions({
  count,
  page,
  perPage,
  allRows,
  onTableChange,
}) {
  return {
    selectableRows: "none",
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: false,
    responsive: "vertical",
    serverSide: true,
    count,
    page,
    rowsPerPage: perPage,
    rowsPerPageOptions: rowsPerPageOptionsFor(count, perPage, allRows),
    filterType: "textField",
    onTableChange,
    setTableProps: () => ({ stickyHeader: true, size: "small" }),
    textLabels: {
      body: { noMatch: "No hay registros por mostrar" },
      pagination: {
        next: "Página siguiente",
        previous: "Página anterior",
        rowsPerPage: "Filas por página:",
        displayRows: "de",
      },
      toolbar: {
        downloadCsv: "Descargar CSV de esta página",
        viewColumns: "Ver columnas",
      },
    },
  };
}

export default function ServiciosSolicitados() {
  const classes = useStyles();
  const serviceColumns = [
    {
      name: "codigoServicio",
      label: "ID servicio",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { width: 78, minWidth: 78, whiteSpace: "nowrap" } }),
        setCellProps: (value) => ({
          style: { width: 78, minWidth: 78, whiteSpace: "nowrap" },
          className: String(value).startsWith("M-") ? classes.mutuaCell : classes.serviceCell,
        }),
        customBodyRender: (value) => <Typography variant="body2" className={classes.serviceId}>{value}</Typography>,
      },
    },
    {
      name: "tipoServicio",
      label: "Servicio",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { width: 102, minWidth: 102 } }),
        setCellProps: () => ({ style: { width: 102, minWidth: 102 }, className: classes.serviceTypeCell }),
        customBodyRender: (value) => <Typography variant="body2" className={classes.serviceType}>{value}</Typography>,
      },
    },
    {
      name: "tipoVehiculo",
      label: "Vehículo",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { width: 82, minWidth: 82 } }),
        setCellProps: () => ({ style: { width: 82, minWidth: 82 }, className: classes.vehicleCell }),
        customBodyRender: (value) =>
          value ? (
            <Typography variant="body2">{value}</Typography>
          ) : (
            <Typography color="textSecondary">—</Typography>
          ),
      },
    },
    {
      name: "pacientes",
      label: "Pacientes",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { minWidth: 112 } }),
        setCellProps: () => ({ style: { minWidth: 112, whiteSpace: "normal" } }),
        customBodyRender: (value) => (
          <PatientSummaryCell value={value} classes={classes} />
        ),
      },
    },
    {
      name: "numeroViajes",
      label: "Viajes",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { width: 56, minWidth: 56, textAlign: "center" } }),
        setCellProps: () => ({ style: { width: 56, minWidth: 56, textAlign: "center" }, className: classes.tripCountCell }),
        customBodyRender: (value) => <Typography variant="body2">{value}</Typography>,
      },
    },
    {
      name: "fechaServicio",
      label: "Fecha",
      options: {
        sort: true,
        setCellHeaderProps: () => ({ style: { width: 90, minWidth: 90, whiteSpace: "nowrap" } }),
        setCellProps: () => ({ style: { width: 90, minWidth: 90, whiteSpace: "nowrap" } }),
        customBodyRender: (value) => (
          <Typography variant="body2" className={classes.dateCell}>
            {value || "—"}
          </Typography>
        ),
      },
    },
  ];
  const tripColumns = [
    {
      name: "idViaje",
      label: "ID viaje",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { width: 78, minWidth: 78, whiteSpace: "nowrap" } }),
        setCellProps: () => ({ style: { width: 78, minWidth: 78, whiteSpace: "nowrap" } }),
        customBodyRender: (value) => (
          <Typography variant="body2" className={classes.tripCode}>
            {value}
          </Typography>
        ),
      },
    },
    {
      name: "pacientes",
      label: "Pacientes",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { minWidth: 230 } }),
        setCellProps: () => ({ style: { minWidth: 230, whiteSpace: "normal" } }),
        customBodyRender: (value) => (
          <PatientSummaryCell value={value} classes={classes} />
        ),
      },
    },
    {
      name: "fechaViaje",
      label: "Fecha del viaje",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { width: 150, minWidth: 150, whiteSpace: "nowrap" } }),
        setCellProps: () => ({ style: { width: 150, minWidth: 150, whiteSpace: "nowrap" } }),
        customBodyRender: (value) => (
          <Typography variant="body2" className={classes.tripDate}>
            {value || "—"}
          </Typography>
        ),
      },
    },
  ];
  const [serviceParams, setServiceParams] = useState({
    page: 0,
    perPage: 25,
    allRows: false,
    search: "",
    sortBy: "fechaServicio",
    sortDirection: "desc",
  });
  const [serviceSearchText, setServiceSearchText] = useState("");
  const [tripParams, setTripParams] = useState({
    page: 0,
    perPage: 25,
    allRows: false,
    search: "",
  });
  const [tripSearchText, setTripSearchText] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [hiddenServiceColumns, setHiddenServiceColumns] = useState([]);
  const [hiddenTripColumns, setHiddenTripColumns] = useState([]);
  const [columnsMenu, setColumnsMenu] = useState(null);
  const visibleServiceColumns = applyColumnVisibility(
    serviceColumns,
    hiddenServiceColumns
  );
  const visibleTripColumns = applyColumnVisibility(tripColumns, hiddenTripColumns);

  const openColumnsMenu = (table, event) => {
    setColumnsMenu({ table, anchorEl: event.currentTarget });
  };

  const toggleColumn = (table, name) => {
    const setHiddenColumns =
      table === "services" ? setHiddenServiceColumns : setHiddenTripColumns;
    setHiddenColumns((current) =>
      current.includes(name)
        ? current.filter((columnName) => columnName !== name)
        : [...current, name]
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setServiceParams((current) =>
        current.search === serviceSearchText
          ? current
          : { ...current, page: 0, search: serviceSearchText }
      );
    }, 350);
    return () => clearTimeout(timer);
  }, [serviceSearchText]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTripParams((current) =>
        current.search === tripSearchText
          ? current
          : { ...current, page: 0, search: tripSearchText }
      );
    }, 350);
    return () => clearTimeout(timer);
  }, [tripSearchText]);

  const servicesQuery = useQuery(
    ["serviciosSolicitados", serviceParams],
    () =>
      fetchServiciosSolicitados({
        ...serviceParams,
        page: serviceParams.page + 1,
      }),
    {
      enabled: selectedService === null,
      keepPreviousData: true,
      retry: false,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
    }
  );

  const tripsQuery = useQuery(
    ["viajesDeServicioSolicitado", selectedService, tripParams],
    () =>
      fetchViajesDeServicioSolicitado({
        idServicio: selectedService.idServicio,
        origenServicio: selectedService.origenServicio,
        ...tripParams,
        page: tripParams.page + 1,
      }),
    {
      enabled: selectedService !== null && selectedTrip === null,
      keepPreviousData: true,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const detailQuery = useQuery(
    ["viajeProgramadoDetalle", selectedTrip],
    () =>
      fetchViajeProgramadoDetalle(
        selectedTrip.idViaje,
        selectedTrip.origenServicio
      ),
    {
      enabled: selectedTrip !== null,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (
      !serviceParams.allRows ||
      servicesQuery.isFetching ||
      servicesQuery.isPreviousData ||
      servicesQuery.isError ||
      !servicesQuery.data?.pagination
    ) {
      return;
    }
    const allRowsValue = Math.max(servicesQuery.data.pagination.total, 1);
    if (serviceParams.perPage !== allRowsValue) {
      setServiceParams((current) =>
        current.allRows ? { ...current, page: 0, perPage: allRowsValue } : current
      );
    }
  }, [
    serviceParams.allRows,
    serviceParams.perPage,
    servicesQuery.data,
    servicesQuery.isError,
    servicesQuery.isFetching,
    servicesQuery.isPreviousData,
  ]);

  useEffect(() => {
    if (
      !tripParams.allRows ||
      tripsQuery.isFetching ||
      tripsQuery.isPreviousData ||
      tripsQuery.isError ||
      !tripsQuery.data?.pagination
    ) {
      return;
    }
    const allRowsValue = Math.max(tripsQuery.data.pagination.total, 1);
    if (tripParams.perPage !== allRowsValue) {
      setTripParams((current) =>
        current.allRows ? { ...current, page: 0, perPage: allRowsValue } : current
      );
    }
  }, [
    tripParams.allRows,
    tripParams.perPage,
    tripsQuery.data,
    tripsQuery.isError,
    tripsQuery.isFetching,
    tripsQuery.isPreviousData,
  ]);

  const [initialViewLoaded, setInitialViewLoaded] = useState(false);
  const initialViewLoadingRef = useRef(true);

  useLayoutEffect(() => {
    mostrarSwalTramitando();

    return () => {
      if (initialViewLoadingRef.current) {
        initialViewLoadingRef.current = false;
        cerrarSwalTramitando();
      }
    };
  }, []);

  useEffect(() => {
    if (
      initialViewLoadingRef.current &&
      servicesQuery.isFetchedAfterMount &&
      !servicesQuery.isFetching
    ) {
      initialViewLoadingRef.current = false;
      setInitialViewLoaded(true);
      cerrarSwalTramitando();
    }
  }, [servicesQuery.isFetchedAfterMount, servicesQuery.isFetching]);

  const hideEmptyServicesWhileFetching =
    !initialViewLoaded ||
    (servicesQuery.isFetching && !(servicesQuery.data?.servicios?.length > 0));
  const hideEmptyTripsWhileFetching =
    !initialViewLoaded ||
    (tripsQuery.isFetching && !(tripsQuery.data?.viajes?.length > 0));

  const handleServiceTableChange = (action, state) => {
    if (action === "changePage") {
      setServiceParams((current) => ({ ...current, page: state.page }));
    } else if (action === "changeRowsPerPage") {
      const allRows =
        state.rowsPerPage ===
        Math.max(servicesQuery.data?.pagination.total || 0, 1);
      setServiceParams((current) => ({
        ...current,
        page: 0,
        perPage: state.rowsPerPage,
        allRows,
      }));
    } else if (action === "search") {
      setServiceSearchText(state.searchText || "");
    } else if (action === "sort") {
      setServiceParams((current) => ({
        ...current,
        page: 0,
        sortBy: state.sortOrder.name || "fechaServicio",
        sortDirection: state.sortOrder.direction || "desc",
      }));
    }
  };

  const handleTripTableChange = (action, state) => {
    if (action === "changePage") {
      setTripParams((current) => ({ ...current, page: state.page }));
    } else if (action === "changeRowsPerPage") {
      const allRows =
        state.rowsPerPage === Math.max(tripsQuery.data?.pagination.total || 0, 1);
      setTripParams((current) => ({
        ...current,
        page: 0,
        perPage: state.rowsPerPage,
        allRows,
      }));
    } else if (action === "search") {
      setTripSearchText(state.searchText || "");
    }
  };

  const openService = (rowMeta) => {
    const serviceIndex = Number.isInteger(rowMeta?.dataIndex)
      ? rowMeta.dataIndex
      : rowMeta?.rowIndex;
    const service = (servicesQuery.data?.servicios || [])[serviceIndex];
    if (service) {
      setTripParams({ page: 0, perPage: 25, allRows: false, search: "" });
      setTripSearchText("");
      setSelectedService(service);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const returnToServices = () => {
    setSelectedTrip(null);
    setSelectedService(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const returnToTrips = () => {
    setSelectedTrip(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectTrip = (rowMeta) => {
    const tripIndex = Number.isInteger(rowMeta?.dataIndex)
      ? rowMeta.dataIndex
      : rowMeta?.rowIndex;
    const trip = (tripsQuery.data?.viajes || [])[tripIndex];
    if (trip && trip.idViaje !== null && trip.idViaje !== undefined) {
      setSelectedTrip({
        idViaje: trip.idViaje,
        origenServicio: selectedService.origenServicio,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const serviceOptions = tableOptions({
    count: servicesQuery.data ? servicesQuery.data.pagination.total : 0,
    page: serviceParams.page,
    perPage: serviceParams.perPage,
    allRows: serviceParams.allRows,
    onTableChange: handleServiceTableChange,
  });
  serviceOptions.onRowClick = (_rowData, rowMeta) => openService(rowMeta);
  serviceOptions.sortOrder = {
    name: serviceParams.sortBy,
    direction: serviceParams.sortDirection,
  };

  const tripOptions = tableOptions({
    count: tripsQuery.data ? tripsQuery.data.pagination.total : 0,
    page: tripParams.page,
    perPage: tripParams.perPage,
    allRows: tripParams.allRows,
    onTableChange: handleTripTableChange,
  });
  tripOptions.onRowClick = (_rowData, rowMeta) => selectTrip(rowMeta);

  const renderTableControls = ({
    table,
    columns,
    hiddenColumns,
    rows,
    filename,
    searchValue,
    onSearchChange,
    searchLabel,
  }) => {
    const menuOpen = columnsMenu?.table === table;
    const visibleCount = columns.filter(
      (column) =>
        column.options?.display !== "excluded" &&
        !hiddenColumns.includes(column.name)
    ).length;

    return (
      <>
        <div className={classes.tableControls}>
          <div className={classes.tableActions}>
            <Button
              aria-label="Descargar CSV de esta página"
              size="small"
              variant="outlined"
              startIcon={<GetAppIcon />}
              disabled={!Array.isArray(rows) || rows.length === 0}
              onClick={() => downloadPageCsv(columns, rows || [], hiddenColumns, filename)}
            >
              Descargar CSV
            </Button>
            <Button
              aria-label="Elegir columnas"
              size="small"
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={(event) => openColumnsMenu(table, event)}
            >
              Elegir columnas
            </Button>
          </div>
          <PatientSearchField
            className={classes.tableToolbarSearch}
            value={searchValue}
            onChange={onSearchChange}
            label={searchLabel}
          />
        </div>
        <Menu
          anchorEl={menuOpen ? columnsMenu.anchorEl : null}
          open={menuOpen}
          onClose={() => setColumnsMenu(null)}
          keepMounted
        >
          {columns
            .filter(
              (column) =>
                column.options?.display !== "excluded" &&
                column.options?.viewColumns !== false
            )
            .map((column) => {
              const checked = !hiddenColumns.includes(column.name);
              const disabled = checked && visibleCount <= 1;
              return (
                <MenuItem
                  key={column.name}
                  dense
                  disabled={disabled}
                  onClick={() => toggleColumn(table, column.name)}
                >
                  <Checkbox checked={checked} color="primary" size="small" />
                  <ListItemText primary={column.label} />
                </MenuItem>
              );
            })}
        </Menu>
      </>
    );
  };

  return (
    <main
      className={
        selectedTrip !== null
          ? `${classes.layout} ${classes.detailLayout}`
          : classes.layout
      }
    >
      {selectedTrip !== null ? (
        <>
          <Button
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={returnToTrips}
          >
            Volver a los viajes del servicio
          </Button>
          {detailQuery.isFetching && (
            <LinearProgress className={classes.progress} />
          )}
          {detailQuery.error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={() => detailQuery.refetch()}>
                  Reintentar
                </Button>
              }
            >
              {detailQuery.error.message}
            </Alert>
          ) : (
            detailQuery.data && <ViajeDetalleCard viaje={detailQuery.data} />
          )}
        </>
      ) : selectedService !== null ? (
        <>
          <div className={classes.serviceHeader}>
            <Button
              className={classes.backButton}
              startIcon={<ArrowBackIcon />}
              onClick={returnToServices}
            >
              Volver a servicios
            </Button>
            <div className={classes.serviceHeading}>
              <div className={classes.heading}>
                <Typography component="h1" variant="h5" className={classes.title}>
                  Servicio {selectedService.codigoServicio}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedService.tipoServicio} · {selectedService.tipoVehiculo || "Vehículo pendiente"} · {selectedService.fechaServicio || "Fecha pendiente"}
                </Typography>
              </div>
              <div className={classes.tableTitle}>
                <Typography variant="body2" color="textSecondary">
                  Viajes asociados
                </Typography>
                <Chip
                  label={selectedService.numeroViajes}
                  className={classes.resultCount}
                  size="small"
                />
              </div>
            </div>
          </div>
          <Typography component="h2" variant="h6" className={classes.heading}>
            Viajes
          </Typography>
          {tripsQuery.isFetching && (
            <LinearProgress className={classes.progress} />
          )}
          {tripsQuery.error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={() => tripsQuery.refetch()}>
                  Reintentar
                </Button>
              }
            >
              {tripsQuery.error.message}
            </Alert>
          ) : (
              !tripsQuery.isLoading &&
              !hideEmptyTripsWhileFetching &&
              tripsQuery.data && (
              <>
                {renderTableControls({
                  table: "trips",
                  columns: tripColumns,
                  hiddenColumns: hiddenTripColumns,
                  rows: tripsQuery.data.viajes,
                  filename: tripParams.allRows
                    ? "ViajesServicio-todos.csv"
                    : `ViajesServicio-pagina-${tripParams.page + 1}.csv`,
                  searchValue: tripSearchText,
                  onSearchChange: setTripSearchText,
                  searchLabel: "Buscar paciente en este servicio",
                })}
                <div className={classes.table}>
                  <MUIDataTable
                    title=""
                    data={tripsQuery.data.viajes}
                    columns={visibleTripColumns}
                    options={tripOptions}
                    components={{ TableToolbar: () => null }}
                  />
                </div>
              </>
            )
          )}
        </>
      ) : (
        <>
          <div className={classes.pageHeader}>
            <div className={classes.heading}>
              <Typography component="h1" variant="h4" className={classes.title}>
                Servicios solicitados
              </Typography>
              <Typography variant="body2" color="textSecondary" className={classes.intro}>
                Selecciona un servicio para consultar sus viajes y pacientes.
              </Typography>
            </div>
          </div>
          {servicesQuery.isFetching && (
            <LinearProgress className={classes.progress} />
          )}
          {servicesQuery.error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={() => servicesQuery.refetch()}>
                  Reintentar
                </Button>
              }
            >
              {servicesQuery.error.message}
            </Alert>
          ) : (
              !servicesQuery.isLoading &&
              !hideEmptyServicesWhileFetching &&
              servicesQuery.data && (
              <>
                {renderTableControls({
                  table: "services",
                  columns: serviceColumns,
                  hiddenColumns: hiddenServiceColumns,
                  rows: servicesQuery.data.servicios,
                  filename: serviceParams.allRows
                    ? "ServiciosSolicitados-todos.csv"
                    : `ServiciosSolicitados-pagina-${serviceParams.page + 1}.csv`,
                  searchValue: serviceSearchText,
                  onSearchChange: setServiceSearchText,
                  searchLabel: "Buscar por paciente",
                })}
                <div className={classes.table}>
                  <MUIDataTable
                    title=""
                    data={servicesQuery.data.servicios}
                    columns={visibleServiceColumns}
                    options={serviceOptions}
                    components={{ TableToolbar: () => null }}
                  />
                </div>
              </>
            )
          )}
        </>
      )}
    </main>
  );
}
